import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import moment from 'moment';
import {
    DeliveryDateGroup,
    DeliveryListItem,
    DELIVERY_LIST_TABLE_COLUMNS,
    filterDeliveryDateGroups,
} from '@/modules/Logistics/helpers/LogisticsTableHelper';
import { downloadTableAsExcel } from '@/helpers/PrintHelper';

// Props

export default /*#__PURE__*/_defineComponent({
  __name: 'DeliveryTable',
  props: {
    dataSource: null,
    searchQuery: null,
    loading: { type: Boolean }
  },
  emits: ["updateSearch", "row-click", "delete"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'updateSearch', value: string): void;
    (e: 'row-click', record: DeliveryDateGroup | DeliveryListItem): void;
    (e: 'delete', ids: number[], onConfirm: () => void): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    dataSource: DeliveryDateGroup[];
    searchQuery: string;
    loading: boolean;
};



// Emits


// State
const selectedRowKeys = ref<Array<string | number>>([]);
const expandedRowKeys = ref<Array<string | number>>([]);

// Table columns
const columns = computed(() => {
    return DELIVERY_LIST_TABLE_COLUMNS();
});

// Computed
const filteredData = computed(() => {
    return filterDeliveryDateGroups(props.dataSource, props.searchQuery);
});

// Row selection config
const rowSelection = computed(() => ({
    selectedRowKeys: selectedRowKeys.value,
    onChange: (keys: Array<string | number>) => {
        handleSelectionChange(keys);
    },
}));

// Methods
function handleSearchChange(e: any) {
    emit('updateSearch', e.target.value);
}

function toggleExpand(record: DeliveryDateGroup | DeliveryListItem) {
    const index = expandedRowKeys.value.indexOf(record.id);
    if (index >= 0) {
        expandedRowKeys.value.splice(index, 1);
    } else {
        expandedRowKeys.value.push(record.id);
    }
}

/**
 * Keep date-group selection in sync with its children:
 * selecting a date row selects all of its delivery lists and expands the group;
 * deselecting removes its delivery lists.
 */
function handleSelectionChange(keys: Array<string | number>) {
    const previous = new Set(selectedRowKeys.value);
    const next = new Set(keys);

    for (const group of filteredData.value) {
        const wasSelected = previous.has(group.id);
        const isSelected = next.has(group.id);

        if (isSelected && !wasSelected) {
            group.children.forEach((child) => next.add(child.id));

            if (!expandedRowKeys.value.includes(group.id)) {
                expandedRowKeys.value.push(group.id);
            }
        } else if (!isSelected && wasSelected) {
            group.children.forEach((child) => next.delete(child.id));
        }
    }

    selectedRowKeys.value = Array.from(next);
}

function customRow(record: DeliveryDateGroup | DeliveryListItem) {
    return {
        on: {
            click: () => emit('row-click', record),
        },
        style: {
            cursor: 'pointer',
        },
    };
}

function handleDeleteSelected() {
    // Only individual delivery lists (numeric keys) can be deleted, not date group rows
    const deliveryListIds = selectedRowKeys.value.filter((key): key is number => typeof key === 'number');
    emit('delete', deliveryListIds, () => (selectedRowKeys.value = []));
}

function handleDownloadExcel() {
    const exportColumns = columns.value.filter((col) => 'dataIndex' in col && col.dataIndex);
    downloadTableAsExcel(exportColumns as any, filteredData.value, 'delivery-lists');
}

function rowClassName(record: DeliveryDateGroup | DeliveryListItem) {
    const today = moment().format('DD.MM.YYYY');
    return record.date === today ? 'today-row' : '';
}

return { __sfc: true,props, emit, selectedRowKeys, expandedRowKeys, columns, filteredData, rowSelection, handleSearchChange, toggleExpand, handleSelectionChange, customRow, handleDeleteSelected, handleDownloadExcel, rowClassName }
}

})